import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Link as RouterLink } from 'react-router-dom';
import { PROCESS, LANDING_ASSETS, LANDING_COLORS } from '../../content/landingContent';

/** Single step card — number, title, description centered */
const StepCard = ({ step }) => (
  <Box
    sx={{
      flex: 1,
      border: `1px solid ${LANDING_COLORS.borderLight}`,
      borderRadius: '12px',
      p: { xs: 2.5, md: 3 },
      bgcolor: '#fff',
      textAlign: 'center',
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontSize: { xs: 18, md: 20 },
        color: '#b5b5b5',
        mb: 1,
      }}
    >
      {step.num}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontWeight: 700,
        fontSize: { xs: 18, md: 20 },
        color: LANDING_COLORS.purple,
        mb: 1.5,
      }}
    >
      {step.title}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontSize: { xs: 14, md: 15 },
        color: LANDING_COLORS.textMuted,
        lineHeight: 1.6,
      }}
    >
      {step.text}
    </Typography>
  </Box>
);

/** Purple circle arrow between step cards — desktop only */
const StepArrow = () => (
  <Box
    sx={{
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: '50%',
      bgcolor: LANDING_COLORS.purple,
      color: '#fff',
    }}
  >
    <ArrowForwardIcon sx={{ fontSize: 18 }} />
  </Box>
);

const ProcessSection = () => (
  <Box id="process" component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
    <Container maxWidth="lg">
      {/* Top — text left, illustration right */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
          mb: { xs: 5, md: 8 },
        }}
      >
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography
            sx={{
              fontFamily: 'Cabin, sans-serif',
              fontWeight: 700,
              fontSize: { xs: 26, md: 36 },
              color: LANDING_COLORS.purple,
              mb: 2,
            }}
          >
            {PROCESS.title}
          </Typography>

          {PROCESS.description.map((para) => (
            <Typography
              key={para.slice(0, 40)}
              sx={{
                fontFamily: 'Cabin, sans-serif',
                fontSize: { xs: 15, md: 16 },
                color: LANDING_COLORS.textMuted,
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              {para}
            </Typography>
          ))}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
            <Button
              href={PROCESS.ctaHref}
              variant="contained"
              sx={{
                bgcolor: LANDING_COLORS.purpleBtn,
                borderRadius: '6px',
                px: 3,
                py: 1.25,
                fontFamily: 'Cabin, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: LANDING_COLORS.purpleDark, boxShadow: 'none' },
              }}
            >
              {PROCESS.ctaLabel}
            </Button>
            <Button
              component={RouterLink}
              to="/calculator"
              variant="outlined"
              startIcon={<CalculateIcon />}
              sx={{
                borderColor: LANDING_COLORS.purple,
                color: LANDING_COLORS.purple,
                borderRadius: '6px',
                px: 3,
                py: 1.25,
                fontFamily: 'Cabin, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                textTransform: 'none',
              }}
            >
              AB Test Hesapla
            </Button>
          </Box>
        </Box>

        {/* Single illustration — desktop only */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={LANDING_ASSETS.processIllustration}
            alt="CRO analiz ve dönüşüm iyileştirme"
            sx={{
              width: '100%',
              maxWidth: 480,
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      {/* Divider — mobile only */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          borderTop: `1px solid ${LANDING_COLORS.borderLight}`,
          mb: 4,
        }}
      />

      {/* Bottom — process step cards */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'flex' },
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'stretch' },
          gap: { xs: 2, md: 2 },
        }}
      >
        {PROCESS.steps.map((step, index) => (
          <React.Fragment key={step.num}>
            <StepCard step={step} />
            {index < PROCESS.steps.length - 1 && <StepArrow />}
          </React.Fragment>
        ))}
      </Box>
    </Container>
  </Box>
);

export default ProcessSection;
